import {
  createAsyncThunk,
  createSlice,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { httpGet, httpPost } from '@/utils/helpers/httpHelper';
import { AppState } from '@/store/store';
import CONFIG from 'config';
import { checkPermission } from '../Auth/authSlice';

export type Campaign = {
  slug: string;
  name: string;
  description: string;
  partnerName: string;
  cities: number[];
  images: ImageType[];
  endDate: string;
  subCategory: number;
  partnerLogo: string;
};

export type CampaignDetail = Campaign & {
  discountRate: number;
  discountedPrice: number;
  documentPath: string;
  partner: Partner;
  price: number;
  siteURL: string;
  category: number;
  subCategory: number;
  id: string;
  isPromocodeExist: boolean;
  rate: number;
  canRate: boolean;
  canApply: boolean;
  rating: {
    userScore: number;
    userDescription: string;
  };
};

export type Partner = {
  name: string;
  description: string;
  logoPath: string;
};

export type CategoryCount = {
  campaignCount: number;
  subCategoryValue: number;
};

export type Option = {
  label: string;
  value: string;
};
export type ImageType = {
  uploadedImagePath: string;
  order: number;
};

export type Choices = {
  category: string;
  label: string;
  value: number;
}[];

export type GroupSelectOption = {
  label: string;
  value: number;
  subCategories?: GroupSelectOption[];
};
export const fetchAllCampaigns: any = createAsyncThunk(
  'campaigns/fetchAllCampaigns',
  async (arg, { getState, dispatch }) => {
    dispatch(fetchServiceCategoryCount());
    const { campaign }: any = getState();
    let filterObject: any = {};
    if (campaign.selectedCities?.length > 0) {
      filterObject.cities = campaign.selectedCities;
    }

    if (campaign.breadCrumb?.subCategory) {
      filterObject.subCategories = [campaign.breadCrumb?.subCategory];
    }

    if (campaign.selectedCategories?.length > 0) {
      filterObject.subCategories = campaign.selectedCategories;
    }
    const { data } = await httpPost(
      `${CONFIG.ENDPOINTS.campaigns}?page=${campaign.page}&limit=${campaign.numPerPage}&orderBy=${campaign.sort}&search=${campaign.searchValue}`,
      filterObject
    );
    return data;
  }
);

export const fetchServiceCategoryCount: any = createAsyncThunk(
  'campaigns/fetchServiceCategoryCount',
  async (arg, { getState }) => {
    const { campaign }: any = getState();
    let filterObject: any = {};
    if (campaign.selectedCities?.length > 0) {
      filterObject.cities = campaign.selectedCities;
    }
    if (campaign.selectedCategories?.length > 0) {
      filterObject.subCategories = campaign.selectedCategories;
    }
    const { data } = await httpPost(
      `${CONFIG.ENDPOINTS.campaignCount}?search=${campaign.searchValue}`,
      filterObject
    );

    return data;
  }
);

export const fetchCampaignDetail: any = createAsyncThunk(
  'campaigns/fetchCampaignDetail',
  async (slug, { dispatch }) => {
    dispatch(fetchCities());
    const { data } = await httpGet<any>(
      `${CONFIG.ENDPOINTS.singleCampaign}${slug}`
    );

    // const res = await httpGet<any>(
    //   `${CONFIG.ENDPOINTS.campaignBase}/${data.data.id}/isPromoCodeExist`
    // );

    dispatch(fetchPromoCode(data.data.id));

    return { ...data };
  }
);

export const fetchPromoCode: any = createAsyncThunk(
  'campaigns/fetchPromoCode',
  async (id) => {
    const { data } = await httpGet<any>(
      `${CONFIG.ENDPOINTS.campaignBase}/${id}/isPromoCodeExist`
    );

    return data;
  }
);

export const fetchCampaigns: any = createAsyncThunk(
  'campaigns/fetchCampaigns',
  async () => {
    const { data } = await httpPost(`${CONFIG.ENDPOINTS.campaigns}`, {});

    return data;
  }
);

export const rateCampaign: any = createAsyncThunk(
  'campaigns/rateCampaign',
  async (rateData) => {
    const { data } = await httpPost(
      `${CONFIG.ENDPOINTS.campaignRating}`,
      rateData
    );

    return data;
  }
);

export const fetchCities: any = createAsyncThunk(
  'campaigns/fetchCities',
  async () => {
    const { data } = await httpGet(`${CONFIG.ENDPOINTS.cities}`);

    return data;
  }
);

export const fetchBanners: any = createAsyncThunk(
  'campaigns/fetchBanners',
  async (arg, { dispatch }) => {
    await dispatch(checkPermission());

    const { data } = await httpGet(`${CONFIG.ENDPOINTS.banners}?type=home`);
    return data;
  }
);

export const fetchServiceCategories: any = createAsyncThunk(
  'campaigns/fetchServiceCategories',
  async () => {
    const { data } = await httpGet(`${CONFIG.ENDPOINTS.serviceCategories}`);
    return data;
  }
);

const campaignAdapter = createEntityAdapter<Campaign>({
  selectId: (campaign) => campaign.slug,
});

export const campaignSlice = createSlice({
  name: 'campaigns',
  initialState: campaignAdapter.getInitialState({
    loading: true,
    allCampaignsLoading: true,
    campaignsLoading: true,
    error: false,
    page: 1,
    numPerPage: 9,
    sort: 'createdAt',
    direction: 'desc',
    totalCount: 9,
    id: null,
    campaigns: null as Campaign[],
    singleCampaign: null as CampaignDetail,
    banners: null as any,
    cities: null as Option[],
    serviceCategories: [] as GroupSelectOption[],
    selectedCities: [] as number[],
    selectedCategories: [] as number[],
    searchValue: '' as string,
    categoryCount: null as CategoryCount[],
    inputsClear: false as boolean,
    breadCrumb: null as { category: number; subCategory: number },
  }),
  reducers: {
    clearSingleCampaign: (state) => {
      state.singleCampaign = null;
    },
    changeBreadCrumbCategory: (state, action) => {
      state.breadCrumb = action.payload;
    },

    changeSelectedCities: (state, action) => {
      state.selectedCities = action.payload;
      state.inputsClear = false;
    },
    changeSelectedCategories: (state, action) => {
      state.selectedCategories = action.payload;
      state.inputsClear = false;
    },
    changeSearchValue: (state, action) => {
      state.searchValue = action.payload;
      state.inputsClear = false;
    },
    clearAllSearchInputs: (state) => {
      state.selectedCities = [];
      state.selectedCategories = [];
      state.breadCrumb = null;
      state.searchValue = '';
      state.inputsClear = true;
    },

    clearSelectedCategories: (state) => {
      state.selectedCategories = [];
      state.breadCrumb = null;
    },

    changePage: (state, action) => {
      state.page = action.payload;
      state.inputsClear = false;
    },
    changeNumPerPage: (state, action) => {
      state.numPerPage = action.payload;
    },
    changeSort: (state, action) => {
      state.sort =
        action.payload.direction === 'none'
          ? ''
          : `${action.payload.name}_${action.payload.direction}`;
    },
    changeAllFilters: (state, action) => {
      state.sort = action.payload.sort;
      state.page = action.payload.page;
      state.numPerPage = action.payload.perPage;
    },
  },
  extraReducers: {
    [fetchCampaigns.pending](state) {
      state.campaignsLoading = true;
    },
    [fetchCampaigns.fulfilled](state, { payload }) {
      state.campaignsLoading = false;

      campaignAdapter.setAll(state, payload.data.items);
      state.totalCount = payload?.totalCount;
    },
    [fetchCampaigns.rejected](state) {
      state.campaignsLoading = false;
      state.error = true;
    },
    [fetchAllCampaigns.pending](state) {
      state.allCampaignsLoading = true;
    },
    [fetchAllCampaigns.fulfilled](state, { payload }) {
      campaignAdapter.setAll(state, payload.data.items);
      state.totalCount = payload?.data.totalCount;
      state.allCampaignsLoading = false;
    },
    [fetchAllCampaigns.rejected](state) {
      state.allCampaignsLoading = false;
      state.error = true;
    },

    [fetchCampaignDetail.pending](state) {
      state.singleCampaign = null;
      state.loading = true;
    },
    [fetchCampaignDetail.fulfilled](state, { payload }) {
      state.singleCampaign = {
        ...payload.data,
        isPromocodeExist: payload.isPromocodeExist,
      };
      state.loading = false;
    },
    [fetchCampaignDetail.rejected](state) {
      state.loading = false;
      state.error = true;
    },
    [fetchPromoCode.pending](state) {
      state.loading = true;
    },
    [fetchPromoCode.fulfilled](state, { payload }) {
      (state.singleCampaign.isPromocodeExist = payload.data.isExist),
        (state.loading = false);
    },
    [fetchPromoCode.rejected](state) {
      (state.singleCampaign.isPromocodeExist = false), (state.loading = false);
      state.error = true;
    },

    [fetchCities.pending](state) {
      state.loading = true;
    },
    [fetchCities.fulfilled](state, { payload }) {
      state.cities = payload;
      state.loading = false;
    },
    [fetchCities.rejected](state) {
      state.loading = false;
      state.error = true;
    },

    [fetchServiceCategories.pending](state) {
      state.loading = true;
    },
    [fetchServiceCategories.fulfilled](state, { payload }) {
      state.serviceCategories = payload;
      state.loading = false;
    },
    [fetchServiceCategories.rejected](state) {
      state.loading = false;
      state.error = true;
    },

    [fetchServiceCategoryCount.pending](state) {
      state.loading = true;
    },
    [fetchServiceCategoryCount.fulfilled](state, { payload }) {
      state.categoryCount = payload.data;
      state.loading = false;
    },
    [fetchServiceCategoryCount.rejected](state) {
      state.loading = false;
      state.error = true;
    },

    [fetchBanners.pending](state) {
      state.loading = true;
    },
    [fetchBanners.fulfilled](state, { payload }) {
      state.banners = payload.data;
      state.loading = false;
    },
    [fetchBanners.rejected](state) {
      state.loading = false;
      state.error = true;
    },
  },
});

export const {
  changePage,
  changeNumPerPage,
  changeSort,
  changeAllFilters,
  changeSelectedCities,
  changeSelectedCategories,
  changeSearchValue,
  clearAllSearchInputs,
  clearSelectedCategories,
  changeBreadCrumbCategory,
  clearSingleCampaign,
} = campaignSlice.actions;
export const campaignSelectors = campaignAdapter.getSelectors<AppState>(
  (state) => state.campaign
);
campaignAdapter.getSelectors<AppState>((state) => state.campaign);

// export const { selectAll, selectById, selectIds, selectEntities } = companySelectors;

export default campaignSlice.reducer;
